import React, { useEffect, useState, useContext } from 'react'
import { useQuery } from '@tanstack/react-query'
import { createColumnHelper } from '@tanstack/react-table'
import {
    ReloadIcon,
    MagnifyingGlassIcon,
    Cross2Icon,
    MixerHorizontalIcon,
    DownloadIcon, // Import DownloadIcon for the export button
} from '@radix-ui/react-icons' // Ensure DownloadIcon is available or use another
import { useSearchParams } from 'react-router-dom'

import useTitle from '../../../hooks/useTitle'
import { Button } from '../../../components/ui/button'
import axiosInstance from '../../../api/axiosInstance'
import { AuthContext } from '../../../contexts/auth-context'
import GenericTable from '../../../components/generic-data-table/GenericTable'
import DataTableShimmer from '../../../components/data-table/DataTableShimmer'
import { toast } from 'sonner'

// Assuming you have a toast notification library (e.g., from shadcn/ui)
// import { toast } from '../../../components/ui/use-toast' // If you have a toast component
// If you don't have one, you can use simple `alert` for now.

const columnHelper = createColumnHelper()

const INITIAL_FILTERS = {
    filter: '',
    status: '',
    gender: '',
    startDate: '',
    endDate: '',
    search: ''
}

function BusinessReportsPurchaseTrends() {
    const { auth } = useContext(AuthContext)
    const businessId = auth.user._id // Assuming auth.user._id is the businessId for this user
    useTitle('Customers Activity')

    const [searchParams, setSearchParams] = useSearchParams()

    const getFiltersFromURL = () => ({
        filter: searchParams.get('filter') || '',
        status: searchParams.get('status') || '',
        gender: searchParams.get('gender') || '',
        startDate: searchParams.get('startDate') || '',
        endDate: searchParams.get('endDate') || '',
        search: searchParams.get('search') || ''
    })

    const [filterInputs, setFilterInputs] = useState(getFiltersFromURL)
    const [activeFilters, setActiveFilters] = useState(getFiltersFromURL)
    const [customersDemographics, setCustomersDemographics] = useState([])

    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        totalCustomers: 0,
        totalPages: 1,
    })

    // NEW STATE FOR EXPORTING
    const [isExporting, setIsExporting] = useState(false)

    const handleInputChange = (e) => {
        const { name, value } = e.target
        setFilterInputs((prev) => ({ ...prev, [name]: value }))
    }

    // Fetching data for the table
    const { data, isLoading, error, refetch } = useQuery({
        queryKey: [
            'businessCustomersDemographics',
            businessId,
            pagination.page,
            pagination.limit,
            activeFilters,
        ],
        queryFn: async () => {
            const params = {
                // businessId is handled by the backend from req.user
                page: pagination.page,
                limit: pagination.limit,
            }

            for (const [key, value] of Object.entries(activeFilters)) {
                if (value) {
                    params[key] = value
                }
            }
            const response = await axiosInstance.get('/reports/purchase-trends', { params })
            return response.data
        },
        keepPreviousData: true,
    })

    useEffect(() => {
        if (data?.data?.docs) {
            setCustomersDemographics(data.data.docs)

            setPagination((prev) => ({
                ...prev,
                totalCustomers: data.data.totalDocs || 0,
                totalPages: Math.ceil((data.data.totalDocs || 1) / pagination.limit),
            }))
        }
    }, [data])

    useEffect(() => {
        const urlFilters = getFiltersFromURL()
        setFilterInputs(urlFilters)
        setActiveFilters(urlFilters)
        setPagination((prev) => ({ ...prev, page: 1 }))
    }, [searchParams])

    const handleApplyFilters = () => {
        const newParams = new URLSearchParams()
        Object.entries(filterInputs).forEach(([key, value]) => {
            if (value) newParams.set(key, value)
        })
        setSearchParams(newParams)
    }

    const handleClearFilters = () => {
        setFilterInputs(INITIAL_FILTERS)
        setActiveFilters(INITIAL_FILTERS)
        setSearchParams({})
        setPagination((prev) => ({ ...prev, page: 1 }))
    }

    const handlePageChange = (newPage) => {
        setPagination((prev) => ({ ...prev, page: newPage }))
    }

    const handleLimitChange = (newLimit) => {
        setPagination((prev) => ({
            ...prev,
            limit: parseInt(newLimit, 10),
            page: 1,
        }))
    }

    const handleRefresh = () => {
        setFilterInputs(getFiltersFromURL())
        setActiveFilters(getFiltersFromURL())
        setPagination({
            page: 1,
            limit: 10,
            totalCustomers: 0,
            totalPages: 1,
        })
        refetch()
    }

    // NEW: handleDataExport function
    const handleDataExport = async () => {
        setIsExporting(true)
        try {
            const params = {}
            // Pass activeFilters as query parameters for the export
            for (const [key, value] of Object.entries(activeFilters)) {
                if (value) {
                    params[key] = value
                }
            }

            // Make the API call to your export endpoint
            const response = await axiosInstance.get('reports/download-loyal-customer', {
                params,
                responseType: 'blob', // IMPORTANT: Specify responseType as 'blob' for binary files
            })

            // Extract filename from Content-Disposition header if available
            const contentDisposition = response.headers['content-disposition']
            let filename = `customers_report_${Date.now()}.xlsx` // Default filename
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/)
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1]
                }
            }

            // Create a Blob from the response data
            const url = window.URL.createObjectURL(
                new Blob([response.data], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                })
            )

            // Create a temporary link element to trigger the download
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', filename) // Set the download filename
            document.body.appendChild(link)
            link.click() // Programmatically click the link
            link.remove() // Clean up the temporary link
            window.URL.revokeObjectURL(url) // Revoke the object URL to free memory

            // Show success message (if you have a toast notification system)
            // toast({
            //   title: 'Export Successful',
            //   description: 'Customer data has been exported to Excel.',
            //   variant: 'success',
            // });

            toast.success('Customer data has been exported successfully')
        } catch (error) {
            console.error('Error exporting data:', error)
            // Handle different error types (e.g., network error vs. API error)
            let errorMessage = 'Failed to export data.'
            if (error.response && error.response.data) {
                // Try to read error message from the blob if it's an API error
                const reader = new FileReader()
                reader.onload = function () {
                    try {
                        const errorJson = JSON.parse(reader.result)
                        errorMessage = errorJson.message || errorMessage
                    } catch (e) {
                        // Not a JSON error, use default message
                    }
                }
                // Read the blob as text
                reader.readAsText(error.response.data)
            } else if (error.message) {
                errorMessage = error.message
            }

            // Show error message
            // toast({
            //   title: 'Export Failed',
            //   description: errorMessage,
            //   variant: 'destructive',
            // });
            alert(`Export Failed: ${errorMessage}`) // Fallback alert
        } finally {
            setIsExporting(false)
        }
    }

    const columns = React.useMemo(
        () => [
            columnHelper.accessor('user.name', { header: 'Name', cell: (info) => info.getValue() || 'N/A' }),
            columnHelper.accessor('user.email', { header: 'Email', cell: (info) => info.getValue() || 'N/A' }),
            columnHelper.accessor('user.mobile', {
                header: 'Mobile',
                cell: (info) => info.getValue() || 'N/A',
            }),
            columnHelper.accessor('quantity_purchased', {
                header: 'Purchased',
                cell: (info) => info.getValue() || '0',
            }),
            columnHelper.accessor('points', {
                header: 'Points', cell: (info) => info.getValue() || '0'
            }),
            //   columnHelper.accessor('totalSpentAmount', {
            //     header: 'SpentAmount' ,cell: (info) => info.getValue() || '0'
            //   }),
            //   columnHelper.accessor('lastVisit', {
            //     header: 'LastVisit',
            //     cell: (info) => (info.getValue() ? new Date(info.getValue()).toLocaleString() : 'N/A'),
            //   }),
        ],
        []
    )

    return (
        <div className="p-6 bg-gray-50 min-h-screen space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                <h1 className="text-2xl font-bold text-gray-900">Purchase Trends</h1>
                {/* <Button onClick={handleRefresh} variant="outline" disabled={isLoading} className="gap-2">
          <ReloadIcon className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button> */}
            </div>

            {/* Filters */}
            <div className="p-4 bg-white rounded-lg border space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4">
                    <div className="relative xl:col-span-2">
                        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                            name="filter"
                            placeholder="Search name, email, mobile..."
                            value={filterInputs.filter}
                            onChange={handleInputChange}
                            onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters()}
                            className="w-full pl-9 pr-4 py-2 text-sm border rounded-md focus:ring-2 focus:ring-theme-color-800"
                        />
                    </div>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-2">
                    {/* Left: Apply + Clear */}
                    <div className="flex flex-row flex-wrap gap-2">
                        <Button onClick={handleApplyFilters} size="sm" className="bg-theme-color-800 hover:bg-theme-color-700 text-white">
                            <MixerHorizontalIcon className="mr-1 h-4 w-4" />
                            Apply
                        </Button>
                        <Button onClick={handleClearFilters} variant="ghost" size="sm" className="text-red-600">
                            <Cross2Icon className="mr-1 h-4 w-4" />
                            Clear
                        </Button>
                    </div>

                    {/* Right: Export + Select */}
                    <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                        <Button
                            className="bg-theme-color-800 hover:bg-theme-color-700 w-full sm:w-auto"
                            onClick={handleDataExport}
                            disabled={isExporting}
                        >
                            {isExporting ? (
                                <>
                                    <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />
                                    Exporting...
                                </>
                            ) : (
                                <>
                                    <DownloadIcon className="mr-2 h-4 w-4" />
                                    Export data
                                </>
                            )}
                        </Button>
                        <div className="relative inline-block mr-3">
                            <select
                                id="itemsPerPage"
                                name="itemsPerPage"
                                value={pagination.limit}
                                onChange={(e) => handleLimitChange(e.target.value)}
                                // disabled={loading}
                                className="block w-full sm:w-auto pl-3 pr-12 py-2 text-sm border-gray-300
               focus:outline-none focus:ring-indigo-500 focus:border-indigo-500
               rounded-md shadow-sm transition duration-150 ease-in-out"
                                style={{ WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none' }}
                            >
                                <option value="10">10 per page</option>
                                <option value="25">25 per page</option>
                                <option value="50">50 per page</option>
                                <option value="100">100 per page</option>
                            </select>

                            {/* custom arrow icon */}
                            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-800">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-4 w-4"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                </svg>
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {isLoading ? (
                <DataTableShimmer columns={5} />
            ) : error ? (
                <div className="text-center py-12 px-6 border border-red-200 rounded-lg bg-red-50">
                    <p className="text-red-600">{error.message}</p>
                    <Button onClick={handleRefresh} variant="outline" className="mt-4">
                        Try Again
                    </Button>
                </div>
            ) : !customersDemographics.length ? (
                <div className="text-center py-12 px-6 border border-dashed border-gray-300 rounded-lg bg-white">
                    <h3 className="mt-3 text-lg font-semibold text-gray-800">No Customers Found</h3>
                    <p className="mt-1 text-sm text-gray-500">
                        {Object.values(activeFilters).some(Boolean)
                            ? 'No customers match your filter criteria.'
                            : "You don't have any customers yet."}
                    </p>
                </div>
            ) : (
                <GenericTable
                    columns={columns}
                    data={customersDemographics}
                    pagination={pagination}
                    onPageChange={handlePageChange}
                    onLimitChange={handleLimitChange}
                    isLoading={isLoading}
                />
            )}
        </div>
    )
}

export default BusinessReportsPurchaseTrends
